/**
 * T0广告看板数据 Hook（CLAUDE.md §Hooks 规范：数据获取逻辑封装在自定义 Hooks）。
 *
 * 职责：
 *   - 进页拉取筛选条选项（GET /tencent/t0-ad-board/options）并初始化默认筛选：
 *     代理商默认取「新客大盘」（2026-10-26 需求：全部名称解析代理汇总，排除
 *     自定义账户集合行；原为第一个非 YK/YR/PR 值）；
 *     时间节点（2026-10-30 起由单一下拉改为 天日期/小时/分钟 三级级联）默认：
 *     天日期=当天（无当天数据兜底最新有数据日）、小时=当前小时（缺则该天
 *     最大小时）、分钟="03"（缺则该小时最早分钟）；
 *   - 已应用筛选（filters）变更时拉取看板数据（GET /tencent/t0-ad-board/data，
 *     合计 + 明细一次返回）；
 *   - 代理商 / 时间节点为必选（无「全部」），两者齐备才发数据请求。
 *
 * 级联说明：options.data_time_list 即 tx_t0_ad_board_latest 表内实际存在的
 *   分钟级时点（最近 7 天倒序），天日期 / 小时 / 分钟三级选项全部由它按定宽
 *   格式（yyyy-MM-dd HH:mm）切分派生，故三级选项天然「只取表内有数据的值」；
 *   父级变更时子级按默认规则重取（省市区级联语义），最终仍拼装为完整
 *   data_time 透传后端（查询接口零改动）。
 *
 * 数据源：tx_t0_ad_board_latest（auto_strategy 第 5 跳分钟级快照）。
 */

import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../services/apiClient";
import {
  T0_AD_BOARD_ALL_DAILI,
  type T0AdBoardData,
  type T0AdBoardOptions,
  getT0AdBoardData,
  getT0AdBoardOptions,
} from "../services/tencent/t0AdBoard";
import { pushToast } from "./useToast";

/** 看板筛选条件（daili / data_time 必选；account_id / ad_id 空串 = 全部）。 */
export interface T0AdBoardFilters {
  daili: string;
  data_time: string;
  account_id: string;
  ad_id: string;
}

/** 时间节点级联选项（由 data_time_list 派生）：天日期倒序、小时 / 分钟升序。 */
export interface T0AdBoardTimeCascade {
  dsList: string[];
  hourList: string[];
  minuteList: string[];
}

/** 当前时间节点三级选中值（空串 = 未选 / 选项未加载）。 */
export interface T0AdBoardTimeParts {
  ds: string;
  hh: string;
  mm: string;
}

/** 级联索引：天日期 → 小时 → 分钟清单。 */
type TimeCascadeMap = Map<string, Map<string, string[]>>;

/** 分钟默认值（快照通常于每小时 03 分首次落盘）。 */
const DEFAULT_MINUTE = "03";

/** 计算代理商默认值：优先「新客大盘」（后端 options 首项恒为该值），
 *  缺则兜底首项；空列表返回空串。 */
function pickDefaultDaili(dailiList: string[]): string {
  return dailiList.includes(T0_AD_BOARD_ALL_DAILI)
    ? T0_AD_BOARD_ALL_DAILI
    : (dailiList[0] ?? "");
}

/** 拆分定宽 data_time（"yyyy-MM-dd HH:mm"）为 天日期 / 小时 / 分钟 三段。 */
function splitDataTime(t: string): T0AdBoardTimeParts {
  return { ds: t.slice(0, 10), hh: t.slice(11, 13), mm: t.slice(14, 16) };
}

/** 本地今日天日期（yyyy-MM-dd，零填充）。 */
function todayDs(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/** 本地当前小时（HH，零填充两位）。 */
function currentHh(): string {
  return String(new Date().getHours()).padStart(2, "0");
}

/** 由 data_time 列表构建级联索引：天日期保持入参倒序（最新在前），
 *  小时 / 分钟升序去重（零填充定宽，字符串序即数值序）。 */
function buildTimeCascade(list: string[]): TimeCascadeMap {
  const raw: TimeCascadeMap = new Map();
  for (const t of list) {
    const { ds, hh, mm } = splitDataTime(t);
    // 防御非定宽脏数据：截断后任一段长度不符即跳过
    if (ds.length !== 10 || hh.length !== 2 || mm.length !== 2) continue;
    let hours = raw.get(ds);
    if (!hours) {
      hours = new Map();
      raw.set(ds, hours);
    }
    const mins = hours.get(hh);
    if (mins) {
      if (!mins.includes(mm)) mins.push(mm);
    } else {
      hours.set(hh, [mm]);
    }
  }
  const out: TimeCascadeMap = new Map();
  for (const [ds, hours] of raw) {
    out.set(
      ds,
      new Map(
        [...hours.entries()]
          .sort(([a], [b]) => (a < b ? -1 : 1))
          .map(([hh, mins]) => [hh, [...mins].sort()]),
      ),
    );
  }
  return out;
}

/** 小时默认取值：当前小时在该天有数据则取之，否则取该天最大小时（天无数据返回空串）。 */
function pickHour(cascade: TimeCascadeMap, ds: string): string {
  const hours = [...(cascade.get(ds)?.keys() ?? [])];
  if (hours.length === 0) return "";
  const cur = currentHh();
  return hours.includes(cur) ? cur : hours[hours.length - 1];
}

/** 分钟默认取值："03" 在该小时有数据则取之，否则取该小时最早分钟（无数据返回空串）。 */
function pickMinute(cascade: TimeCascadeMap, ds: string, hh: string): string {
  const mins = cascade.get(ds)?.get(hh) ?? [];
  if (mins.length === 0) return "";
  return mins.includes(DEFAULT_MINUTE) ? DEFAULT_MINUTE : mins[0];
}

/** 级联默认 data_time：天日期=当天（缺则最新有数据日）→ 小时=当前小时
 *  （缺则该天最大）→ 分钟="03"（缺则该小时最早）；任一段取不到返回空串。 */
function pickDefaultDataTime(cascade: TimeCascadeMap): string {
  const dsList = [...cascade.keys()];
  if (dsList.length === 0) return "";
  const ds = dsList.includes(todayDs()) ? todayDs() : dsList[0];
  const hh = pickHour(cascade, ds);
  const mm = pickMinute(cascade, ds, hh);
  return hh && mm ? `${ds} ${hh}:${mm}` : "";
}

export function useT0AdBoardData() {
  const [options, setOptions] = useState<T0AdBoardOptions | null>(null);
  /** 已应用筛选（null = 选项未加载完成，尚未初始化默认值）。 */
  const [filters, setFilters] = useState<T0AdBoardFilters | null>(null);
  const [data, setData] = useState<T0AdBoardData | null>(null);
  const [loading, setLoading] = useState(false);

  /** 级联索引（options 加载后构建一次；分钟级时点 ~数百条，构建开销可忽略）。 */
  const cascade = useMemo(
    () => buildTimeCascade(options?.data_time_list ?? []),
    [options],
  );

  // 进页：拉选项 → 初始化默认筛选（代理商默认「新客大盘」；时间节点按级联默认规则）。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await getT0AdBoardOptions();
        if (cancelled) return;
        setOptions(opts);
        const daili = pickDefaultDaili(opts.daili_list);
        const dataTime = pickDefaultDataTime(buildTimeCascade(opts.data_time_list));
        if (daili && dataTime) {
          setFilters({ daili, data_time: dataTime, account_id: "", ad_id: "" });
        }
      } catch (err) {
        if (!cancelled) {
          pushToast(
            err instanceof ApiError ? err.message : "拉取看板筛选选项失败",
            "error",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 已应用筛选变更 → 拉看板数据（daili / data_time 必齐备）。
  useEffect(() => {
    if (!filters || !filters.daili || !filters.data_time) return;
    let cancelled = false;
    setLoading(true);
    getT0AdBoardData({
      daili: filters.daili,
      data_time: filters.data_time,
      account_id: filters.account_id || undefined,
      ad_id: filters.ad_id || undefined,
    })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          pushToast(
            err instanceof ApiError ? err.message : "拉取看板数据失败",
            "error",
          );
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  /** 当前三级选中值（filters 未初始化时全空串）。 */
  const timeParts: T0AdBoardTimeParts = splitDataTime(filters?.data_time ?? "");

  /** 当前选中下的三级选项：天日期全量（倒序）；小时 / 分钟依赖父级选中，
   *  父级未选时为空清单（页面据此隐藏对应下拉，实现逐级出现）。 */
  const timeCascade: T0AdBoardTimeCascade = {
    dsList: [...cascade.keys()],
    hourList: timeParts.ds ? [...(cascade.get(timeParts.ds)?.keys() ?? [])] : [],
    minuteList:
      timeParts.ds && timeParts.hh
        ? [...(cascade.get(timeParts.ds)?.get(timeParts.hh) ?? [])]
        : [],
  };

  /** 级联变更（省市区语义）：改父级时子级按默认规则重取（小时=当前小时
   *  →缺则最大；分钟="03"→缺则最早），拼装完整 data_time 入 filters 触发刷新；
   *  拼装结果与现值相同则不更新（避免重复请求）。 */
  function applyTimePart(level: "ds" | "hh" | "mm", value: string) {
    setFilters((prev) => {
      if (!prev) return prev;
      let { ds, hh, mm } = splitDataTime(prev.data_time);
      if (level === "ds") {
        ds = value;
        hh = pickHour(cascade, ds);
        mm = pickMinute(cascade, ds, hh);
      } else if (level === "hh") {
        hh = value;
        mm = pickMinute(cascade, ds, hh);
      } else {
        mm = value;
      }
      if (!ds || !hh || !mm) return prev; // 防御：任一段取不到不应用
      const data_time = `${ds} ${hh}:${mm}`;
      return prev.data_time === data_time ? prev : { ...prev, data_time };
    });
  }

  return {
    options,
    filters,
    setFilters,
    data,
    loading,
    timeCascade,
    timeParts,
    applyTimePart,
  };
}
